import { Box } from "@mui/material"
import { useMemo, useState } from "react"
import { intl } from "src/internationalization/intl"
import { formatCurrencyBRL } from "src/lib/transformers/ClientsOverviewInfo"
import type { DailyExecutionPoint } from "src/services/api/opportunities/types"
import { Typography } from "../../basics"
import {
    buildAdvisorExecutionTimelines,
    type AdvisorTimelineSeries,
    type ExecutionTimelinePoint,
} from "./buildExecutionTimeline"

export interface ExecutionOverTimeChartProps {
    createdDate?: string
    dueDate?: string
    series?: DailyExecutionPoint[]
}

const formatShortDate = (iso: string): string => {
    const [, m, d] = iso.split("-")
    return `${d}/${m}`
}

const formatLongDate = (iso: string): string => iso.split("-").reverse().join("/")

/** Distinct palette for advisor lines (brand-adjacent, readable on dark). */
const ADVISOR_LINE_COLORS = [
    "#A2CDED",
    "#7DCEA0",
    "#F5B041",
    "#AF7AC5",
    "#5DADE2",
    "#F1948A",
    "#48C9B0",
    "#F7DC6F",
    "#85929E",
    "#E59866",
]

const colorForIndex = (i: number): string => ADVISOR_LINE_COLORS[i % ADVISOR_LINE_COLORS.length]

const PLOT_H_PX = 140
const LABEL_H_PX = 22
const PAD_L = 8
const PAD_R = 8
const PAD_T = 12
const PAD_B = 4
const VB_W = 600
const VB_H = PLOT_H_PX

const linePath = (
    points: ExecutionTimelinePoint[],
    xAt: (i: number) => number,
    yAt: (v: number) => number,
): string =>
    points
        .map((p, i) => `${i === 0 ? "M" : "L"} ${xAt(i).toFixed(2)} ${yAt(p.cumulative_net).toFixed(2)}`)
        .join(" ")

const ExecutionOverTimeChart = ({ createdDate, dueDate, series }: ExecutionOverTimeChartProps) => {
    const advisors = useMemo(
        () =>
            buildAdvisorExecutionTimelines(
                createdDate,
                dueDate,
                series,
                intl("objectiveMonitoringNoAdvisor"),
                intl("objectiveMonitoringTimelineTotal"),
            ),
        [createdDate, dueDate, series],
    )
    const [hoverIdx, setHoverIdx] = useState<number | null>(null)

    if (advisors.length === 0) {
        return (
            <Typography variant="caption" color="secondary">
                {intl("objectiveMonitoringTimelineEmpty")}
            </Typography>
        )
    }

    const dates = advisors[0].points
    const n = dates.length
    const maxCum = Math.max(...advisors.map((a) => a.total_net), 0)
    const yMax = maxCum > 0 ? maxCum : 1
    const plotW = VB_W - PAD_L - PAD_R
    const plotH = VB_H - PAD_T - PAD_B
    const grandTotal = advisors.reduce((s, a) => s + a.total_net, 0)

    const xAt = (i: number) => (n <= 1 ? PAD_L + plotW / 2 : PAD_L + (i / (n - 1)) * plotW)
    const yAt = (v: number) => PAD_T + plotH - (v / yMax) * plotH
    const xPct = (i: number) => `${(xAt(i) / VB_W) * 100}%`
    const yPct = (v: number) => `${(yAt(v) / VB_H) * 100}%`

    const tickIdx = n <= 3 ? dates.map((_, i) => i) : [0, Math.floor((n - 1) / 2), n - 1]
    const activeDate: string | null = hoverIdx != null ? dates[hoverIdx]?.date ?? null : null
    const tickAlign = (i: number): "left" | "center" | "right" => {
        if (i === 0) return "left"
        if (i === n - 1) return "right"
        return "center"
    }

    const hoverRows: Array<{ series: AdvisorTimelineSeries; point: ExecutionTimelinePoint; color: string }> =
        hoverIdx == null
            ? []
            : advisors
                  .map((seriesItem, i) => ({
                      series: seriesItem,
                      point: seriesItem.points[hoverIdx],
                      color: colorForIndex(i),
                  }))
                  .filter((r) => r.point != null)
                  .sort((a, b) => b.point.cumulative_net - a.point.cumulative_net)

    return (
        <Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 1,
                    mb: 0.75,
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="caption"
                        sx={{ color: "neutral.lighter", fontWeight: 700, display: "block" }}
                    >
                        {intl("objectiveMonitoringTimelineTitle")}
                    </Typography>
                    <Typography variant="caption" color="quaternary">
                        {intl("objectiveMonitoringTimelineHintByAdvisor")}
                    </Typography>
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 700, flexShrink: 0 }}>
                    {formatCurrencyBRL(grandTotal)}
                </Typography>
            </Box>

            <Box
                sx={{
                    width: "100%",
                    bgcolor: "neutral.darkest",
                    borderRadius: 1,
                    border: "1px solid rgba(255, 255, 255, 0.06)",
                    overflow: "hidden",
                    position: "relative",
                }}
                onMouseLeave={() => setHoverIdx(null)}
            >
                <Box sx={{ width: "100%", height: PLOT_H_PX, position: "relative" }}>
                <svg
                    width="100%"
                    height="100%"
                    viewBox={`0 0 ${VB_W} ${VB_H}`}
                    preserveAspectRatio="none"
                    style={{ display: "block" }}
                >
                    <line
                        x1={PAD_L}
                        y1={PAD_T + plotH}
                        x2={PAD_L + plotW}
                        y2={PAD_T + plotH}
                        stroke="rgba(255,255,255,0.08)"
                        strokeWidth={1}
                    />
                    {advisors.map((adv, ai) => {
                        const d = linePath(adv.points, xAt, yAt)
                        const color = colorForIndex(ai)
                        return d ? (
                            <path
                                key={adv.key}
                                d={d}
                                fill="none"
                                stroke={color}
                                strokeWidth={2}
                                strokeLinejoin="round"
                                strokeLinecap="round"
                                vectorEffect="non-scaling-stroke"
                            />
                        ) : null
                    })}
                    {dates.map((p, i) => {
                        const x = xAt(i)
                        const half = n <= 1 ? plotW / 2 : plotW / (2 * Math.max(n - 1, 1))
                        return (
                            <rect
                                key={p.date}
                                x={x - half}
                                y={PAD_T}
                                width={Math.max(half * 2, 4)}
                                height={plotH}
                                fill={hoverIdx === i ? "rgba(255,255,255,0.06)" : "transparent"}
                                onMouseEnter={() => setHoverIdx(i)}
                                style={{ cursor: "crosshair" }}
                            />
                        )
                    })}
                    {hoverIdx != null ? (
                        <line
                            x1={xAt(hoverIdx)}
                            y1={PAD_T}
                            x2={xAt(hoverIdx)}
                            y2={PAD_T + plotH}
                            stroke="rgba(255,255,255,0.25)"
                            strokeWidth={1}
                            strokeDasharray="3 3"
                            pointerEvents="none"
                            vectorEffect="non-scaling-stroke"
                        />
                    ) : null}
                </svg>

                {/* Bolinhas em HTML — o SVG com preserveAspectRatio=none alongava os círculos */}
                {hoverIdx != null
                    ? hoverRows.map((r) => (
                          <Box
                              key={`dot-${r.series.key}`}
                              sx={{
                                  position: "absolute",
                                  left: xPct(hoverIdx),
                                  top: yPct(r.point.cumulative_net),
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: r.color,
                                  border: "1.5px solid #0A0B0C",
                                  transform: "translate(-50%, -50%)",
                                  pointerEvents: "none",
                                  zIndex: 1,
                              }}
                          />
                      ))
                    : null}

                {activeDate && hoverRows.length > 0 ? (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            right: 8,
                            pointerEvents: "none",
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: "rgba(10, 11, 12, 0.85)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            maxWidth: 320,
                            maxHeight: PLOT_H_PX - 16,
                            overflow: "hidden",
                        }}
                    >
                        <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.25 }}>
                            {formatLongDate(activeDate)}
                        </Typography>
                        {hoverRows.slice(0, 6).map((r) => (
                            <Box
                                key={r.series.key}
                                sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}
                            >
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        bgcolor: r.color,
                                        flexShrink: 0,
                                        mt: "3px",
                                    }}
                                />
                                <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0 }}>
                                    {r.series.label}
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 700, flexShrink: 0 }}>
                                    {formatCurrencyBRL(r.point.cumulative_net)}
                                </Typography>
                            </Box>
                        ))}
                        {hoverRows.length > 6 ? (
                            <Typography variant="caption" color="quaternary">
                                {intl("objectiveMonitoringTimelineMoreAdvisors", {
                                    values: { count: String(hoverRows.length - 6) },
                                })}
                            </Typography>
                        ) : null}
                    </Box>
                ) : null}
                </Box>

                {/* Datas em HTML para não distorcer com preserveAspectRatio=none */}
                <Box sx={{ position: "relative", height: LABEL_H_PX, px: 0 }}>
                    {tickIdx.map((i) => {
                        const align = tickAlign(i)
                        return (
                            <Typography
                                key={`tick-${dates[i].date}`}
                                variant="caption"
                                sx={{
                                    position: "absolute",
                                    left: xPct(i),
                                    top: 2,
                                    transform:
                                        align === "left"
                                            ? "translateX(0)"
                                            : align === "right"
                                              ? "translateX(-100%)"
                                              : "translateX(-50%)",
                                    color: "rgba(255,255,255,0.55)",
                                    fontSize: "0.7rem",
                                    lineHeight: 1.2,
                                    whiteSpace: "nowrap",
                                    fontVariantNumeric: "tabular-nums",
                                }}
                            >
                                {formatShortDate(dates[i].date)}
                            </Typography>
                        )
                    })}
                </Box>
            </Box>

            {/* Legenda */}
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.25,
                    mt: 1,
                    alignItems: "center",
                }}
            >
                {advisors.map((adv, i) => (
                    <Box
                        key={adv.key}
                        sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}
                        title={`${adv.label}: ${formatCurrencyBRL(adv.total_net)}`}
                    >
                        <Box
                            sx={{
                                width: 10,
                                height: 3,
                                borderRadius: 1,
                                bgcolor: colorForIndex(i),
                                flexShrink: 0,
                            }}
                        />
                        <Typography variant="caption" color="quaternary" noWrap>
                            {adv.label}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, flexShrink: 0 }}>
                            {formatCurrencyBRL(adv.total_net)}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

export { ExecutionOverTimeChart }
